import React from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { getAssignableOrgRoles } from 'pure-js/libs/organizationMembershipHelpers'
import { OrgRole } from 'pure-js/types/OrganizationMembership'

import { INTERAX_COLORS } from '../../../constants/colors'
import { useTranslation } from '../../../shared/hooks/useIntractionTranslate'
import { orgRoleTranslationKey } from '../orgTranslationKeys'

const ROLE_OPTIONS: OrgRole[] = [OrgRole.GAMER, OrgRole.LEADER, OrgRole.FACILITATOR, OrgRole.ADMIN]

const ROLE_HINT_KEYS: Record<OrgRole, string> = {
  [OrgRole.GAMER]: 'org.team.invite.roleHint.gamer',
  [OrgRole.LEADER]: 'org.team.invite.roleHint.leader',
  [OrgRole.FACILITATOR]: 'org.team.invite.roleHint.facilitator',
  [OrgRole.ADMIN]: 'org.team.invite.roleHint.admin'
}

interface OrgInviteRolePickerProps {
  value: OrgRole
  onChange: (role: OrgRole) => void
  isLeaderRoleOffered: boolean
}

/**
 * Segmented role picker for org admins inviting by email.
 */
export const OrgInviteRolePicker: React.FC<OrgInviteRolePickerProps> = ({ value, onChange, isLeaderRoleOffered }) => {
  const { t } = useTranslation()
  const roleOptions = getAssignableOrgRoles(ROLE_OPTIONS, isLeaderRoleOffered)

  return (
    <Stack spacing={1}>
      <Box
        role="radiogroup"
        aria-label={t('org.members.invite.roleLabel')}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row', md: 'row' },
          gap: { xs: 1, sm: 1, md: 1 },
          p: 0.5,
          borderRadius: '10px',
          backgroundColor: INTERAX_COLORS.offBlack,
          border: `1px solid ${INTERAX_COLORS.white10}`
        }}
      >
        {roleOptions.map((role) => {
          const selected = value === role
          return (
            <Box
              key={role}
              component="button"
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onChange(role)}
              data-testid={`org-invite-role-${role.toLowerCase()}`}
              sx={{
                flex: 1,
                minHeight: { xs: 40, sm: 40, md: 40 },
                px: { xs: 1.5, sm: 2, md: 2 },
                py: 1,
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontFamily: 'inherit',
                fontSize: { xs: 14, sm: 14, md: 14 },
                fontWeight: selected ? 600 : 400,
                color: INTERAX_COLORS.white,
                backgroundColor: selected ? INTERAX_COLORS.white10 : 'transparent',
                boxShadow: selected
                  ? `0px 4px 8px rgba(0, 0, 0, 0.2), inset 0px -1px 1px ${INTERAX_COLORS.white10}`
                  : 'none',
                transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
                '&:hover': {
                  backgroundColor: selected ? INTERAX_COLORS.white10 : INTERAX_COLORS.white04
                },
                '&:focus-visible': {
                  outline: `2px solid ${INTERAX_COLORS.sun}`,
                  outlineOffset: 2
                }
              }}
            >
              {t(orgRoleTranslationKey(role))}
            </Box>
          )
        })}
      </Box>
      <Typography sx={{ fontSize: 12, color: INTERAX_COLORS.thunderGrey, lineHeight: 1.45 }}>
        {t(ROLE_HINT_KEYS[value])}
      </Typography>
    </Stack>
  )
}
